export interface PlanAssistantCitation {
  title: string;
  url: string | null;
  locator: string | null;
}

export interface PlanAssistantResponse {
  response: {
    answer: string;
    citations: PlanAssistantCitation[];
    limitations: string[];
    follow_up_questions: string[];
  };
  continuation_token: string;
  advisor_release_id: string;
}

/** Minimal conversation runtime contract, independent of any route or workspace. */
export interface PlanAssistantConversationContext {
  readonly planReferenceKind: PlanAssistantPlanReferenceKind;
  readonly propertyId: string;
  readonly planId: string;
  readonly planRevision: string;
  readonly locale: string;
}

export function planAssistantContextIdentity(context: PlanAssistantConversationContext): string {
  return [
    context.planReferenceKind,
    context.propertyId,
    context.planId,
    context.planRevision,
    context.locale,
  ].join('\u001f');
}

export type PlanAssistantPlanReferenceKind =
  | 'saved-plan'
  | 'generated-plan'
  | 'simulation-recommended'
  | 'simulation-pukkala-aaf';

export interface PlanAssistantRequest {
  plan_reference_kind: PlanAssistantPlanReferenceKind;
  property_id: string;
  plan_id: string;
  plan_revision: string;
  question: string;
  intent_ids?: PlanAssistantIntent[];
  continuation_token?: string;
}

export type PlanAssistantIntent = 'economics.explain' | 'timing.explain' | 'data_quality.explain';

export type PlanAssistantAsk = (
  request: PlanAssistantRequest,
  signal?: AbortSignal,
) => Promise<PlanAssistantResponse>;

/** Content variants understood by the shared assistant renderer. */
export type AssistantContentDescriptor =
  | { readonly kind: 'placeholder' }
  | {
      readonly kind: 'plan-conversation';
      readonly context: PlanAssistantConversationContext;
      readonly ask: PlanAssistantAsk;
      readonly onContextExpired?: () => void;
    };

export class PlanAssistantRequestError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = 'PlanAssistantRequestError';
  }
}
